import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';

import {
  SkillAvailableCurator,
  SkillCuratorAssignmentRequest,
  SkillCuratorAssignmentResponse,
  SkillCuratorOptionView,
  SkillGroup,
  SpecializationCuratorSelectionValue,
} from '../../../models/skill-learning.model';
import { SKILL_LEARNING_FIRST_INDEX } from '../../../options/skill-learning.option';
import {
  SPECIALIZATION_CURATOR_SELECTION,
  SPECIALIZATION_CURATOR_SOURCE,
  SPECIALIZATION_FORM_ACCEPTED_LEVEL,
  SPECIALIZATION_FORM_FORMAT,
  SPECIALIZATION_FORM_MESSAGE,
  SPECIALIZATION_FORM_SELECTION,
} from '../specialization-form/specialization-form.option';

@Component({
  selector: 'app-specialization-curator-selector',
  templateUrl: './specialization-curator-selector.component.html',
  styleUrls: ['./specialization-curator-selector.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SpecializationCuratorSelectorComponent implements OnInit, OnChanges {

  @Input() public initialCurators: SkillCuratorAssignmentResponse[] = [];
  @Input() public availableCurators: SkillAvailableCurator[] = [];
  @Input() public availableGroups: SkillGroup[] = [];
  @Input() public crossDepartmental = false;
  @Input() public manageableCuratorGroupIds: string[] = [];
  @Input() public initialResponsibleGroupId = SPECIALIZATION_FORM_SELECTION.EMPTY_VALUE;
  @Input() public requireCurator = false;
  @Input() public maxCurators = SPECIALIZATION_CURATOR_SELECTION.UNLIMITED;
  @Input() public curatorLabelKey = SPECIALIZATION_FORM_MESSAGE.DEFAULT_CURATOR_LABEL;
  @Input() public canAssignAnyCuratorLevel = false;
  @Input() public configuredLevelCount: number = null;

  @Output() public selectionChanged = new EventEmitter<SpecializationCuratorSelectionValue>();

  public selectedResponsibleGroupId = SPECIALIZATION_FORM_SELECTION.EMPTY_VALUE;
  public selectedCuratorGroupFilterId = SPECIALIZATION_FORM_SELECTION.EMPTY_VALUE;
  public selectedAssignments: SkillCuratorAssignmentRequest[] = [];
  public localCurators: SkillCuratorOptionView[] = [];
  public externalCurators: SkillCuratorOptionView[] = [];
  public curatorDepartmentOptions: SkillGroup[] = [];
  public responsibleGroups: SkillGroup[] = [];

  private initialized = false;

  public ngOnInit(): void {
    if (!this.initialized) {
      this.initializeSelection();
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!changes.initialCurators
      && !changes.manageableCuratorGroupIds
      && !changes.initialResponsibleGroupId
      && !changes.crossDepartmental) {
      if (changes.availableGroups) {
        this.responsibleGroups = this.buildResponsibleGroups();
      }
      this.refreshView();
      return;
    }
    this.initializeSelection();
  }

  public get hasMultipleResponsibleGroups(): boolean {
    return this.crossDepartmental
      && this.responsibleGroups.length > SPECIALIZATION_CURATOR_SELECTION.SINGLE_GROUP_COUNT;
  }

  public get isResponsibleGroupRequired(): boolean {
    return this.crossDepartmental && !this.selectedResponsibleGroupId;
  }

  public get hasNoCuratorsSelected(): boolean {
    return this.selectedAssignments.length === SPECIALIZATION_CURATOR_SELECTION.EMPTY_COUNT;
  }

  public get hasTooManyCurators(): boolean {
    return this.maxCurators > SPECIALIZATION_CURATOR_SELECTION.UNLIMITED
      && this.selectedAssignments.length > this.maxCurators;
  }

  public get hasExternalCurators(): boolean {
    return this.externalCurators.length > SPECIALIZATION_CURATOR_SELECTION.EMPTY_COUNT;
  }

  public onResponsibleGroupChanged(groupId: string): void {
    this.selectedResponsibleGroupId = groupId || SPECIALIZATION_FORM_SELECTION.EMPTY_VALUE;
    this.selectedCuratorGroupFilterId = SPECIALIZATION_FORM_SELECTION.EMPTY_VALUE;
    this.loadSelectedAssignments();
    this.refreshView();
    this.emitSelection();
  }

  public onCuratorGroupFilterChanged(groupId: string): void {
    this.selectedCuratorGroupFilterId = groupId || SPECIALIZATION_FORM_SELECTION.EMPTY_VALUE;
    this.refreshView();
  }

  public toggleCurator(curatorId: string): void {
    if (!this.canToggleCurator(curatorId)) { return; }
    const index = this.selectedAssignments.findIndex(assignment => assignment.userId === curatorId);
    if (index !== SPECIALIZATION_CURATOR_SELECTION.NOT_FOUND_INDEX) {
      if (this.requireCurator
        && this.selectedAssignments.length <= SPECIALIZATION_CURATOR_SELECTION.SINGLE_GROUP_COUNT) {
        return;
      }
      this.selectedAssignments = this.selectedAssignments.filter(assignment => assignment.userId !== curatorId);
    } else if (this.maxCurators === SPECIALIZATION_CURATOR_SELECTION.UNLIMITED
      || this.selectedAssignments.length < this.maxCurators) {
      this.selectedAssignments = this.selectedAssignments.concat({
        userId: curatorId,
        maxAcceptedLevel: this.resolveMaxAcceptedLevel(this.findDisplayCurator(curatorId)),
      });
    }
    this.refreshView();
    this.emitSelection();
  }

  public canToggleCurator(curatorId: string): boolean {
    if (this.isCuratorSelected(curatorId)) {
      const assignment = this.activeInitialCurators().find(curator => curator.userId === curatorId);
      return !assignment || assignment.removable !== false;
    }
    const candidate = this.findDisplayCurator(curatorId);
    return !!candidate && (this.canAssignAnyCuratorLevel || candidate.eligibleForHeadAssignment !== false);
  }

  public onAcceptedLevelChanged(curatorId: string, value: string | number): void {
    const level = this.normalizeAcceptedLevel(value);
    this.selectedAssignments = this.selectedAssignments.map(assignment => assignment.userId === curatorId
      ? { userId: assignment.userId, maxAcceptedLevel: level }
      : assignment);
    this.refreshView();
    this.emitSelection();
  }

  public trackById(index: number, item: SkillAvailableCurator | SkillGroup): string {
    return item.id;
  }

  public trackByLevel(index: number, level: number): number {
    return level;
  }

  private initializeSelection(): void {
    this.initialized = true;
    this.responsibleGroups = this.buildResponsibleGroups();
    this.selectedResponsibleGroupId = this.resolveInitialResponsibleGroupId();
    this.loadSelectedAssignments();
    this.refreshView();
    this.emitSelection();
  }

  private loadSelectedAssignments(): void {
    this.selectedAssignments = this.activeInitialCurators()
      .filter(curator => !this.crossDepartmental || !this.isDefaultHeadAssignment(curator))
      .map(curator => ({
        userId: curator.userId,
        maxAcceptedLevel: this.normalizeAcceptedLevel(curator.maxAcceptedLevel),
      }));
  }

  private activeInitialCurators(): SkillCuratorAssignmentResponse[] {
    if (!this.crossDepartmental) {
      return this.initialCurators || [];
    }
    if (!this.selectedResponsibleGroupId) {
      return [];
    }
    return (this.initialCurators || []).filter(curator =>
      curator.responsibleGroupId === this.selectedResponsibleGroupId
      || (!curator.responsibleGroupId
        && this.responsibleGroups.length === SPECIALIZATION_CURATOR_SELECTION.SINGLE_GROUP_COUNT));
  }

  private refreshView(): void {
    const allCurators = this.buildDisplayCurators();
    const curators = allCurators
      .filter(curator => !this.selectedCuratorGroupFilterId
        || curator.groupId === this.selectedCuratorGroupFilterId)
      .map(curator => this.buildCuratorOptionView(curator));
    this.localCurators = this.sortCurators(curators.filter(curator =>
      this.isLocalCurator(curator, this.selectedResponsibleGroupId)));
    this.externalCurators = this.sortCurators(curators.filter(curator =>
      !this.isLocalCurator(curator, this.selectedResponsibleGroupId)));
    this.curatorDepartmentOptions = this.buildCuratorDepartmentOptions(allCurators);
  }

  private buildDisplayCurators(): SkillAvailableCurator[] {
    const activeInitial = this.activeInitialCurators();
    const hiddenDefaultHeadIds = this.buildHiddenDefaultHeadIds(activeInitial);
    const candidates = this.buildCuratorCandidates(
      activeInitial,
      this.availableCurators,
      hiddenDefaultHeadIds,
    );
    const usedIds: { [userId: string]: boolean } = {};
    return candidates.filter(curator => {
      const unique = !!curator.id && !usedIds[curator.id];
      if (unique) {
        usedIds[curator.id] = true;
      }
      return unique && (this.canAssignAnyCuratorLevel
        || this.isCuratorSelected(curator.id)
        || this.rawAcceptedLevel(curator) >= this.resolveMinimumAcceptedLevel());
    });
  }

  private buildCuratorOptionView(curator: SkillAvailableCurator): SkillCuratorOptionView {
    const assignment = this.selectedAssignments.find(candidate => candidate.userId === curator.id);
    const defaultHead = this.activeInitialCurators().some(candidate => candidate.userId === curator.id
      && this.isDefaultHeadAssignment(candidate));
    return Object.assign({}, curator, {
      selected: !!assignment,
      defaultHead,
      acceptedLevel: assignment ? assignment.maxAcceptedLevel : this.resolveMinimumAcceptedLevel(),
      acceptedLevelOptions: this.buildAcceptedLevelOptions(curator),
    });
  }

  private buildAcceptedLevelOptions(curator: SkillAvailableCurator): number[] {
    const result: number[] = [];
    for (let level = this.resolveMinimumAcceptedLevel();
         level <= this.resolveMaxAcceptedLevel(curator); level++) {
      result.push(level);
    }
    return result;
  }

  private isCuratorSelected(curatorId: string): boolean {
    return this.selectedAssignments.some(assignment => assignment.userId === curatorId);
  }

  private buildResponsibleGroups(): SkillGroup[] {
    return (this.manageableCuratorGroupIds || []).map(groupId => {
      const group = (this.availableGroups || []).find(candidate => candidate.id === groupId);
      return group || { id: groupId, name: groupId };
    }).sort((left, right) =>
      (left.name || SPECIALIZATION_FORM_SELECTION.EMPTY_VALUE)
        .localeCompare(right.name || SPECIALIZATION_FORM_SELECTION.EMPTY_VALUE));
  }

  private resolveInitialResponsibleGroupId(): string {
    const ids = (this.manageableCuratorGroupIds || []).filter(id => !!id);
    if (this.initialResponsibleGroupId
      && (!this.crossDepartmental || ids.indexOf(this.initialResponsibleGroupId)
        !== SPECIALIZATION_CURATOR_SELECTION.NOT_FOUND_INDEX)) {
      return this.initialResponsibleGroupId;
    }
    return ids.length === SPECIALIZATION_CURATOR_SELECTION.SINGLE_GROUP_COUNT
      ? ids[SKILL_LEARNING_FIRST_INDEX]
      : SPECIALIZATION_FORM_SELECTION.EMPTY_VALUE;
  }

  private findDisplayCurator(curatorId: string): SkillAvailableCurator | null {
    return this.buildDisplayCurators().find(curator => curator.id === curatorId) || null;
  }

  private resolveMaxAcceptedLevel(curator: SkillAvailableCurator | null): number {
    if (this.canAssignAnyCuratorLevel) {
      return SPECIALIZATION_FORM_ACCEPTED_LEVEL.MAX;
    }
    return this.clampAcceptedLevel(this.rawAcceptedLevel(curator));
  }

  private rawAcceptedLevel(curator: SkillAvailableCurator | null): number {
    return curator
      ? curator.maxAvailableAcceptedLevel
        || curator.completedLevels
        || SPECIALIZATION_CURATOR_SELECTION.EMPTY_COUNT
      : SPECIALIZATION_CURATOR_SELECTION.EMPTY_COUNT;
  }

  private normalizeAcceptedLevel(value: string | number): number {
    const parsed = Number(value);
    return this.clampAcceptedLevel(isNaN(parsed) ? this.resolveMinimumAcceptedLevel() : parsed);
  }

  private clampAcceptedLevel(value: number): number {
    return Math.max(this.resolveMinimumAcceptedLevel(),
      Math.min(SPECIALIZATION_FORM_ACCEPTED_LEVEL.MAX, value));
  }

  private resolveMinimumAcceptedLevel(): number {
    return SPECIALIZATION_FORM_ACCEPTED_LEVEL.MIN;
  }

  private buildCuratorFullName(curator: SkillAvailableCurator): string {
    const value = [curator.lastName, curator.firstName]
      .filter(part => !!part)
      .join(SPECIALIZATION_FORM_FORMAT.PERSON_NAME_SEPARATOR)
      .trim();
    return value || curator.fullName || curator.id;
  }

  private toAvailableCurator(curator: SkillCuratorAssignmentResponse): SkillAvailableCurator {
    const person = Object.assign({ id: curator.userId }, curator);
    return {
      id: curator.userId,
      firstName: curator.firstName || SPECIALIZATION_FORM_SELECTION.EMPTY_VALUE,
      lastName: curator.lastName || SPECIALIZATION_FORM_SELECTION.EMPTY_VALUE,
      fullName: this.buildCuratorFullName(person),
      groupId: curator.groupId,
      groupName: curator.groupName,
      groupNameTranslation: curator.groupNameTranslation,
      localDepartment: curator.localDepartment,
      completedLevels: curator.completedLevels || SPECIALIZATION_CURATOR_SELECTION.EMPTY_COUNT,
      maxAvailableAcceptedLevel: curator.maxAcceptedLevel,
    };
  }

  private isDefaultHeadAssignment(curator: SkillCuratorAssignmentResponse): boolean {
    return !!curator.headDefault || curator.source === SPECIALIZATION_CURATOR_SOURCE.HEAD_DEFAULT;
  }

  private buildCuratorCandidates(
    activeInitial: SkillCuratorAssignmentResponse[],
    available: SkillAvailableCurator[],
    hiddenDefaultHeadIds: string[],
  ): SkillAvailableCurator[] {
    const initial = activeInitial
      .filter(curator => hiddenDefaultHeadIds.indexOf(curator.userId)
        === SPECIALIZATION_CURATOR_SELECTION.NOT_FOUND_INDEX)
      .map(curator => this.toAvailableCurator(curator));
    const candidates = (available || [])
      .filter(curator => hiddenDefaultHeadIds.indexOf(curator.id)
        === SPECIALIZATION_CURATOR_SELECTION.NOT_FOUND_INDEX)
      .map(curator => Object.assign({}, curator, { fullName: this.buildCuratorFullName(curator) }));
    return initial.concat(candidates);
  }

  private isLocalCurator(curator: SkillAvailableCurator, responsibleGroupId: string): boolean {
    return responsibleGroupId && curator.groupId
      ? curator.groupId === responsibleGroupId
      : !!curator.localDepartment;
  }

  private sortCurators(curators: SkillCuratorOptionView[]): SkillCuratorOptionView[] {
    return curators.slice().sort((left, right) => {
      const levelDiff = (right.completedLevels || SPECIALIZATION_CURATOR_SELECTION.EMPTY_COUNT)
        - (left.completedLevels || SPECIALIZATION_CURATOR_SELECTION.EMPTY_COUNT);
      return levelDiff || this.buildCuratorFullName(left).localeCompare(this.buildCuratorFullName(right));
    });
  }

  private buildHiddenDefaultHeadIds(curators: SkillCuratorAssignmentResponse[]): string[] {
    return this.crossDepartmental
      ? curators.filter(curator => this.isDefaultHeadAssignment(curator)).map(curator => curator.userId)
      : [];
  }

  private buildCuratorDepartmentOptions(curators: SkillAvailableCurator[]): SkillGroup[] {
    const result: SkillGroup[] = [];
    curators.forEach(curator => {
      if (!curator.groupId || result.some(group => group.id === curator.groupId)) {
        return;
      }
      result.push({
        id: curator.groupId,
        name: curator.groupName || curator.groupId,
        nameTranslation: curator.groupNameTranslation,
      });
    });
    return result.sort((left, right) => (left.name || SPECIALIZATION_FORM_SELECTION.EMPTY_VALUE)
      .localeCompare(right.name || SPECIALIZATION_FORM_SELECTION.EMPTY_VALUE));
  }

  private emitSelection(): void {
    this.selectionChanged.emit({
      responsibleGroupId: this.selectedResponsibleGroupId,
      curatorIds: this.selectedAssignments.map(assignment => assignment.userId),
      curators: this.selectedAssignments.slice(),
    });
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/skill-learning/components/admin/specialization-curator-selector/specialization-curator-selector.component.ts